import { CuffaProductionDifficultyEnum } from 'interfaces/cuffa.interface';

export type Prioridade = { value: string; priority: number };

export const difficultyApiKeys: { [key: string]: string } = {
  [CuffaProductionDifficultyEnum.CONDICAO_DAS_ESTRADAS]: "road_conditions",
  [CuffaProductionDifficultyEnum.NECESSIDADE_CONSTRUCAO_PONTES_OBRAS]: "need_bridge_construction_special_works",
  [CuffaProductionDifficultyEnum.DIFICULDADE_ACESSO_LOCALIDADES]: "difficulty_accessing_localities",
  [CuffaProductionDifficultyEnum.DISTANCIA_CENTROS_CONSUMIDORES]: "distance_consumer_centers",
  [CuffaProductionDifficultyEnum.FALTA_MERCADO_CONSUMIDOR]: "lack_consumer_market",
  [CuffaProductionDifficultyEnum.DIFICULDADE_ACESSAR_CREDITO_RURAL]: "difficulty_accessing_rural_credit",
  [CuffaProductionDifficultyEnum.CONDICOES_SAUDE_MEMBROS_FAMILIA]: "health_conditions_family_members",
  [CuffaProductionDifficultyEnum.FALTA_VEICULOS_TRANSPORTAR_PRODUCAO]: "lack_vehicles_transport_production",
  [CuffaProductionDifficultyEnum.FALTA_VEICULO_APROPRIADO_PERECIVEIS]: "lack_appropriate_vehicle_perishables",
  [CuffaProductionDifficultyEnum.FALTA_MAO_OBRA]: "lack_labor",
  [CuffaProductionDifficultyEnum.CUSTO_PRODUCAO_ELEVADO]: "high_production_cost",
  [CuffaProductionDifficultyEnum.FALTA_INFORMACAO]: "lack_information",
  [CuffaProductionDifficultyEnum.REGULARIZACAO_AMBIENTAL]: "environmental_regularization",
  [CuffaProductionDifficultyEnum.REGULARIZACAO_FISCAL]: "fiscal_regularization",
  [CuffaProductionDifficultyEnum.REGULARIZACAO_FUNDIARIA]: "land_regularization",
  [CuffaProductionDifficultyEnum.REGULARIZACAO_SANITARIA]: "sanitary_regularization",
};

export const reverseDifficultyApiKeys = Object.fromEntries(
  Object.entries(difficultyApiKeys).map(([key, value]) => [value, key])
);

export const desafiosPadrao = Object.values(CuffaProductionDifficultyEnum);

export function parseSavedProductionDifficulties(savedData: any): { novosSelecionados: string[]; novasPrioridades: Prioridade[]; novosOutros: string[] } {
  const novosSelecionados: string[] = [];
  const novasPrioridades: Prioridade[] = [];
  const novosOutros: string[] = [];

  if (!savedData || typeof savedData !== 'object') return { novosSelecionados, novasPrioridades, novosOutros };

  for (const backendKey in savedData) {
    if (backendKey === 'others' && Array.isArray(savedData.others)) {
      savedData.others.forEach((outro: { description: string; priority: number }) => {
        novosOutros.push(outro.description);
        novosSelecionados.push(outro.description);
        novasPrioridades.push({ value: outro.description, priority: outro.priority });
      });
    } else {
      const challengeName = reverseDifficultyApiKeys[backendKey];
      if (challengeName && typeof savedData[backendKey] === 'number') {
        novosSelecionados.push(challengeName);
        novasPrioridades.push({ value: challengeName, priority: savedData[backendKey] });
      }
    }
  }

  novasPrioridades.sort((a, b) => a.priority - b.priority);

  return { novosSelecionados, novasPrioridades, novosOutros };
}

export function buildProductionDifficultiesPayload(prioridades: Prioridade[], outrosDesafios: string[]): any {
  const payload: any = { others: [] };
  prioridades.forEach(p => {
    const apiKey = difficultyApiKeys[p.value as keyof typeof difficultyApiKeys];
    if (apiKey) {
      payload[apiKey] = p.priority;
    } else if (outrosDesafios.includes(p.value)) {
      payload.others.push({ priority: p.priority, description: p.value });
    }
  });
  return payload;
}

export function recomputePrioritiesOnSelect(desafiosSelecionados: string[], prioridades: Prioridade[], nome: string) {
  const estaSelecionado = desafiosSelecionados.includes(nome);
  let novosSelecionados: string[];
  let novasPrioridades: Prioridade[];

  if (estaSelecionado) {
    novosSelecionados = desafiosSelecionados.filter((d) => d !== nome);
    const prioridadesRestantes: Prioridade[] = prioridades.filter((p) => p.value !== nome);
    const prioridadesOrdenadas: Prioridade[] = [...prioridadesRestantes].sort((a, b) => a.priority - b.priority);
    novasPrioridades = prioridadesOrdenadas.map((item, index) => ({ ...item, priority: index + 1 }));
  } else {
    novosSelecionados = [...desafiosSelecionados, nome];
    const novaPrioridade: Prioridade = { value: nome, priority: desafiosSelecionados.length + 1 };
    novasPrioridades = [...prioridades, novaPrioridade];
  }

  return { novosSelecionados, novasPrioridades };
}

export function changePriority(prioridades: Prioridade[], value: string, novaPrioridade: number) {
  const prioridadesCopia = prioridades.map(p => ({ ...p }));
  const itemAtualIndex = prioridadesCopia.findIndex(p => p.value === value);
  const itemAlvoIndex = prioridadesCopia.findIndex(p => p.priority === novaPrioridade);

  if (itemAlvoIndex > -1) {
    prioridadesCopia[itemAlvoIndex].priority = prioridadesCopia[itemAtualIndex]?.priority || 0;
  }
  if (itemAtualIndex > -1) {
    prioridadesCopia[itemAtualIndex].priority = novaPrioridade;
  }
  return prioridadesCopia;
}

export function addOutroLogic(novoOutroRaw: string, desafiosPadraoLocal: string[], outrosDesafios: string[], desafiosSelecionados: string[], prioridades: Prioridade[]) {
  const desafioFormatado = novoOutroRaw.trim().toUpperCase();
  if (!desafioFormatado) return { ok: false, message: 'Descreva o desafio antes de adicionar.' };
  const todosOsDesafiosAtuais = [...desafiosPadraoLocal, ...outrosDesafios];
  if (todosOsDesafiosAtuais.map(d => d.toUpperCase()).includes(desafioFormatado)) return { ok: false, message: 'Este desafio já existe na lista.' };

  const novosOutros = [...outrosDesafios, desafioFormatado];
  const novosSelecionados = [...desafiosSelecionados, desafioFormatado];
  const novaPrioridade = { value: desafioFormatado, priority: desafiosSelecionados.length + 1 };
  const novasPrioridades = [...prioridades, novaPrioridade];
  return { ok: true, novosOutros, novosSelecionados, novasPrioridades };
}

export function removeOutroLogic(desafioRemovido: string, outrosDesafios: string[], desafiosSelecionados: string[], prioridades: Prioridade[]) {
  const novosOutros = outrosDesafios.filter((d) => d !== desafioRemovido);
  const novosSelecionados = desafiosSelecionados.filter((d) => d !== desafioRemovido);
  const prioridadesRestantes: Prioridade[] = prioridades.filter((p) => p.value !== desafioRemovido);
  const prioridadesOrdenadas: Prioridade[] = [...prioridadesRestantes].sort((a, b) => a.priority - b.priority);
  const novasPrioridadesReajustadas: Prioridade[] = prioridadesOrdenadas.map((item, index) => ({ ...item, priority: index + 1 }));
  return { novosOutros, novosSelecionados, novasPrioridadesReajustadas };
}
